import { useParams } from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  Box,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from '@chakra-ui/react';

import ValidationMessage from 'components/validation/ValidationMessage';
import ValidateFile from 'components/fields/shared/Message/components/ValidateFile';

import useFormBinderDisabled from 'hooks/useFormBinderDisabled';

import { useMultiLineRentalAgreementResolver } from './hooks';
import { getHTMLId } from './resolverUtils';
import type { ValidationResolverProps } from './types';

export interface Validation561ResolverProps extends ValidationResolverProps {}

const VALIDATION_ID = '561';
const FORM_STATE_KEY = 'homePersonalForm';

const Validation561Resolver = ({
  section,
  itemKey: propItemKey,
}: Validation561ResolverProps) => {
  const { uuid } = useParams();
  const isBinderDisabled = useFormBinderDisabled(FORM_STATE_KEY);

  const itemKey = propItemKey || 'global';

  const { state, handlers, fieldIds, alertMessage } =
    useMultiLineRentalAgreementResolver({
      section,
      itemKey,
    });

  const htmlId = getHTMLId(VALIDATION_ID, itemKey);

  if (!state.show) return null;

  return (
    <Box>
      <div id={htmlId}></div>
      <div id={fieldIds.attestation}></div>
      <div id={fieldIds.file}></div>

      <Alert status="warning" mt="10px" fontSize="14px">
        <AlertIcon />
        <ValidationMessage message={alertMessage} />
      </Alert>

      <Box mt="15px" p="15px" bg="gray.50" borderRadius="md">
        <FormControl mb="15px" id={fieldIds.attestation} isRequired>
          <FormLabel fontWeight="semibold">
            Broker Attestation: I confirm that all drivers listed on the
            cross-referenced auto policy have driving record ratings exceeding
            DR 3.
          </FormLabel>
          <RadioGroup
            value={state.attestationAnswer || ''}
            onChange={handlers.handleAttestationChange}
            isDisabled={isBinderDisabled}
          >
            <Stack direction="row" spacing={5}>
              <Radio value="yes">Yes – Proceed, no further action required</Radio>
              <Radio value="no">No – Attachment required</Radio>
            </Stack>
          </RadioGroup>
        </FormControl>

        {state.attestationAnswer === 'no' && state.file && (
          <Box id={fieldIds.file}>
            <Text fontSize="sm" color="red.500" fontWeight="medium" mb="10px">
              Mandatory: Upload a valid copy of the lease agreement or prior
              property insurance declaration page.
            </Text>
            <ValidateFile
              uuid={uuid}
              file={state.file}
              validationId={VALIDATION_ID}
              itemKey={itemKey}
              formStateKey={FORM_STATE_KEY}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Validation561Resolver;
